import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, CircularProgress, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CloseIcon from '@mui/icons-material/Close';
import { TypingAnimation } from '../common';
import { useTranslation } from 'react-i18next';

const YouTubeInput = ({
  url,
  setUrl,
  videoTitle,
  loading,
  error,
  handleAnalyze,
  fetchYoutubeTitle
}) => {
  const { t, i18n } = useTranslation();

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    if (newUrl.trim()) {
      fetchYoutubeTitle(newUrl);
    }
  };

  const handleClearTitle = () => {
    setUrl("");
    fetchYoutubeTitle("");
  };

  const animationTexts = [
    `${t('landing.catchphrase1')}<br />${t('landing.catchphrase1_sub')}`,
    `${t('landing.catchphrase2')}<br />${t('landing.catchphrase2_sub')}`,
    `${t('landing.catchphrase3')}<br />${t('landing.catchphrase3_sub')}`
  ];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '800px' },
        mx: 'auto',
        mt: { xs: 3, sm: 4, md: 8 },
        px: { xs: 2, sm: 3, md: 2 },
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: { xs: 3, md: 4 },
        minHeight: { xs: '100px', md: '140px' },
        justifyContent: 'center',
        width: '100%'
      }}>
        <TypingAnimation
          key={i18n.language} // ✅ 언어 변경 시 컴포넌트 리셋
          texts={animationTexts}
          typingSpeed={40}
          pauseDuration={4500}
          fadeOutDuration={600}
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
            lineHeight: { xs: 1.3, md: 1.5 },
            color: 'inherit',
            fontFamily: 'inherit'
          }}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ 
          display: 'flex',
          width: '100%',
          position: 'relative'
        }}>
          {!videoTitle ? (
            <TextField
              fullWidth
              placeholder={t('landing.urlPlaceholder')}
              value={url}
              onChange={handleUrlChange}
              error={Boolean(error)}
              helperText={error}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: '20px 0 0 20px',
                  height: '56px',
                  '& fieldset': {
                    borderColor: '#ccc',
                    borderRight: 'none',
                    transition: 'border-color 0.2s',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: '1px',
                  },
                },
                '& .MuiFormHelperText-root': {
                  mx: 0,
                  mt: 1,
                  fontSize: '0.875rem',
                  color: 'error.main'
                }
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#fff',
                borderRadius: '20px 0 0 20px',
                px: 2,
                height: '56px',
                flex: 1,
                minWidth: 0,
                border: '1px solid #ccc',
                borderRight: 'none',
                '&:hover': { bgcolor: '#fff' }
              }}
            >
              <YouTubeIcon 
                sx={{ 
                  color: '#FF0000',
                  mr: 1,
                  fontSize: '1.5rem',
                  flexShrink: 0
                }} 
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <Typography
                  variant="body1"
                  noWrap
                  sx={{
                    color: '#333',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {videoTitle}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClearTitle}
                sx={{
                  color: '#666',
                  ml: 1,
                  flexShrink: 0,
                  '&:hover': {
                    color: '#f44336',
                    bgcolor: 'rgba(244, 67, 54, 0.08)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={!url.trim() || loading}
            sx={{
              width: '80px',
              flexShrink: 0,
              height: '56px',
              borderRadius: '0 20px 20px 0',
              bgcolor: '#f2f2f2',
              color: '#222',
              fontWeight: 400,
              fontSize: '0.875rem',
              boxShadow: 'none',
              border: '1px solid #ccc',
              borderLeft: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#e0e0e0',
                boxShadow: 'none',
                borderColor: '#1976d2',
              },
              '&.Mui-disabled': {
                bgcolor: '#f2f2f2',
                color: '#bbb'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#888' }} />
            ) : (
              t('analyze.analyzeButton')
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default YouTubeInput; 